import { useState, useEffect } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  adminGetAnswerRules, adminCreateAnswerRule, adminDeleteAnswerRule,
  adminGetQuizQuestions, adminGetConditions, adminGetSystems
} from '@/lib/api';
import { toast } from 'sonner';

const AdminAnswerRules = () => {
  const [rules, setRules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [formData, setFormData] = useState({
    question_id: '',
    answer_value: '',
    target_type: 'condition',
    target_id: '',
    score: 1
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rulesData, questionsData, conditionsData, systemsData] = await Promise.all([
        adminGetAnswerRules(),
        adminGetQuizQuestions(),
        adminGetConditions(),
        adminGetSystems()
      ]);
      setRules(rulesData || []);
      setQuestions(questionsData || []);
      setConditions(conditionsData || []);
      setSystems(systemsData || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionText = (questionId) => {
    const q = questions.find(q => q.id === questionId);
    return q?.question_text?.substring(0, 50) + (q?.question_text?.length > 50 ? '...' : '') || 'Unknown';
  };

  const getAnswerLabel = (questionId, answerValue) => {
    const q = questions.find(q => q.id === questionId);
    const a = q?.answers?.find(a => a.answer_value === answerValue);
    return a?.answer_label || answerValue;
  };

  const getTargetName = (type, id) => {
    if (type === 'condition') {
      const c = conditions.find(c => c.id === id);
      return c?.consumer_label || 'Unknown';
    } else {
      const s = systems.find(s => s.id === id);
      return s?.consumer_label || 'Unknown';
    }
  };

  const filteredRules = selectedQuestion 
    ? rules.filter(r => r.question_id === selectedQuestion)
    : rules;

  const openCreateDialog = () => {
    setFormData({
      question_id: selectedQuestion || questions[0]?.id || '',
      answer_value: '',
      target_type: 'condition',
      target_id: conditions[0]?.id || '',
      score: 1
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminCreateAnswerRule({
        ...formData,
        score: parseInt(formData.score) || 1
      });
      toast.success('Scoring rule created');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm('Delete this scoring rule?')) return;
    try {
      await adminDeleteAnswerRule(rule.id);
      toast.success('Rule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const selectedQuestionAnswers = formData.question_id 
    ? questions.find(q => q.id === formData.question_id)?.answers || []
    : [];

  if (loading) {
    return <div className="text-stone-400 p-8">Loading scoring rules...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Answer Scoring Rules</h1>
          <p className="text-stone-600 text-sm">Map quiz answers to conditions for scoring</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-rule-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Rule
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-[#4A6763]/20 bg-[#4A6763]/5">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-[#4A6763] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div className="text-sm text-stone-700">
              <p className="font-medium mb-1">How Scoring Works</p>
              <p>Each rule assigns a score to a condition or system when a user selects that answer. 
                 The quiz totals all scores and recommends protocols for the top-scoring conditions.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-4 items-center">
        <label className="text-sm text-stone-600">Filter by question:</label>
        <Select value={selectedQuestion} onValueChange={setSelectedQuestion}>
          <SelectTrigger className="w-80" data-testid="filter-question-select">
            <SelectValue placeholder="All questions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All questions</SelectItem>
            {questions.map((q) => (
              <SelectItem key={q.id} value={q.id}>
                Q{q.sort_order + 1}: {q.question_text.substring(0, 40)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedQuestion && (
          <Button variant="ghost" onClick={() => setSelectedQuestion('')} className="text-sm">
            Clear filter
          </Button>
        )}
      </div>

      {filteredRules.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">
              {selectedQuestion ? 'No rules for this question' : 'No scoring rules created yet'}
            </p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline">
              Create Your First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Question</TableHead>
                <TableHead>Answer</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Score</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map((rule) => (
                <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="text-left">
                          <span className="text-sm">{getQuestionText(rule.question_id)}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{questions.find(q => q.id === rule.question_id)?.question_text}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {getAnswerLabel(rule.question_id, rule.answer_value)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge className={rule.target_type === 'condition' ? 'badge-primary' : 'badge-secondary'}>
                        {rule.target_type}
                      </Badge>
                      <span className="text-sm">{getTargetName(rule.target_type, rule.target_id)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-[#4A6763]">+{rule.score}</span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(rule)}
                      className="h-8 w-8 text-stone-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Add Scoring Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Question</label>
                <Select
                  value={formData.question_id}
                  onValueChange={(value) => setFormData({ ...formData, question_id: value, answer_value: '' })}
                >
                  <SelectTrigger data-testid="rule-question-select">
                    <SelectValue placeholder="Select question" />
                  </SelectTrigger>
                  <SelectContent>
                    {questions.map((q) => (
                      <SelectItem key={q.id} value={q.id}>
                        Q{q.sort_order + 1}: {q.question_text.substring(0, 40)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Answer</label>
                <Select
                  value={formData.answer_value}
                  onValueChange={(value) => setFormData({ ...formData, answer_value: value })}
                  disabled={!formData.question_id}
                >
                  <SelectTrigger data-testid="rule-answer-select">
                    <SelectValue placeholder="Select answer" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedQuestionAnswers.map((a) => (
                      <SelectItem key={a.answer_value} value={a.answer_value}>
                        {a.answer_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Target Type</label>
                <Select
                  value={formData.target_type}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    target_type: value,
                    target_id: value === 'condition' ? conditions[0]?.id : systems[0]?.id
                  })}
                >
                  <SelectTrigger data-testid="rule-target-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="condition">Condition</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Target {formData.target_type === 'condition' ? 'Condition' : 'System'}</label>
                <Select
                  value={formData.target_id}
                  onValueChange={(value) => setFormData({ ...formData, target_id: value })}
                >
                  <SelectTrigger data-testid="rule-target-select">
                    <SelectValue placeholder={`Select ${formData.target_type}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {(formData.target_type === 'condition' ? conditions : systems).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.consumer_label || item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Score Points</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.score}
                  onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                  data-testid="rule-score-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={!formData.question_id || !formData.answer_value || !formData.target_id}
                data-testid="save-rule-btn"
              >
                Create Rule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnswerRules;
